<template>
  <div class="space-y-4">
    <div v-if="porscheCentersData" class="grid gap-4 md:grid-cols-3">
      <InputField
        :readonly="handoverView"
        name="insuranceData.referenceNumber"
        :label="t('referenceNumber')"
      />
      <LabeledText :label="t('customerNr')">
        {{ EUROP_CUSTOMER_NUMBER }}
      </LabeledText>
      <InputField name="insuranceData.licencePlate" :label="t('licencePlate')" />
      <InputField
        v-if="!handoverView"
        name="insuranceData.initialDays"
        :label="t('initialDays')"
        type="number"
      />
      <SearchableSelectField
        :model-value="porscheCenter?.userId"
        :name="'insuranceData.porscheCenter'"
        :label="t('porscheCenter')"
        :options="porscheCenterOptions"
        :is-error="!!errorMessage"
        :hint="errorMessage"
        @update:model-value="handleUpdatePorscheCenter"
      />
      <SelectField
        :readonly="handoverView"
        name="insuranceData.europCategory"
        :options="europCategoryOptions"
        :label="t('europCategory')"
        empty-value-to="undefined"
      />
      <InputField
        :readonly="handoverView"
        name="insuranceData.acrissCode"
        :label="t('acrissCode')"
      />
    </div>
    <div v-else class="flex justify-center">
      <Spinner />
    </div>
  </div>
</template>

<script setup lang="ts">
import InputField from '@/components/InputField.vue';
import SelectField from '@/components/SelectField.vue';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useUsersWithQuery } from '@/queries/use-users';
import { EuropVehicleCategory, type PorscheCenter } from '@carvia/ros-client-types';
import { useField } from 'vee-validate';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { EUROP_CUSTOMER_NUMBER } from '../constants';
import LabeledText from '@/components/LabeledText.vue';
import SearchableSelectField from '@/components/SearchableSelectField.vue';
import { sortBy } from 'lodash';

defineProps<{
  handoverView?: boolean;
}>();

const { t } = useI18n();

const {
  value: porscheCenter,
  setValue: setPorscheCenter,
  errorMessage,
} = useField<PorscheCenter | undefined>('insuranceData.porscheCenter');

const handleUpdatePorscheCenter = (userId: string | undefined) => {
  if (!userId) {
    setPorscheCenter(undefined);
    return;
  }
  const porscheCenter = porscheCenters.value?.find((porscheCenter) => porscheCenter.id === userId);
  setPorscheCenter({
    userId: porscheCenter!.id,
    displayName: porscheCenter!.firstName + ' ' + porscheCenter!.lastName,
  });
};

const { data: porscheCentersData } = useUsersWithQuery({
  query: 'Porsche Zentrum',
  limit: 500,
});
const porscheCenters = useFlattenPaginatedData(porscheCentersData);
const porscheCenterOptions = computed(() => [
  { label: t('noPorscheCenter'), value: '' },
  ...sortBy(
    porscheCenters.value?.map((porscheCenter) => ({
      label: porscheCenter.firstName + ' ' + porscheCenter.lastName,
      value: porscheCenter.id,
    })) ?? [],
    'label',
  ),
]);

const europCategoryOptions = computed(() => {
  return [
    { label: t('noCategory'), value: null },
    ...Object.values(EuropVehicleCategory).map((category) => ({
      label: t(`europCategories.${category}`),
      value: category,
    })),
  ];
});
</script>

<i18n lang="json">
{
  "en": {
    "referenceNumber": "Reference number",
    "customerNr": "Agenturnummer / Kunden-Nr.",
    "licencePlate": "Licence plate",
    "porscheCenter": "Porsche Center",
    "initialDays": "Initial days",
    "europCategory": "Europ category",
    "acrissCode": "ACRISS Code",
    "noCategory": "- No category -",
    "noPorscheCenter": "- No Porsche Center -"
  },
  "de": {
    "referenceNumber": "Referenznummer / Aktenzeichen",
    "customerNr": "Agenturnummer / Kunden-Nr.",
    "licencePlate": "Kennzeichen",
    "porscheCenter": "Porschezentrum",
    "initialDays": "Initiale Tagesanzahl",
    "europCategory": "Europ Kategorie",
    "acrissCode": "ACRISS Code",
    "noCategory": "- Keine Kategorie -",
    "noPorscheCenter": "- Kein Porschezentrum -"
  },
  "es": {
    "referenceNumber": "Referencia / n.º de expediente",
    "customerNr": "N.º agencia / N.º cliente",
    "licencePlate": "Matrícula",
    "porscheCenter": "Centro Porsche",
    "initialDays": "Días iniciales",
    "europCategory": "Categoría Europ",
    "acrissCode": "Código ACRISS",
    "noCategory": "- Sin categoría -",
    "noPorscheCenter": "- Sin centro Porsche -"
  }
}
</i18n>
